import { supabase } from './supabaseClient';

// Shared helper for Edge Function callers: ensures we use a fresh access token.
// This reduces "UNAUTHORIZED" errors after mobile backgrounding/tab switching,
// where the in-memory access token may be expired and needs a refresh.

const withTimeout = async (promise, ms, label) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const err = new Error(`${label} timed out after ${ms}ms`);
      err.code = 'TIMEOUT';
      reject(err);
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

let refreshInFlight = null;
let cachedAccessToken = null;
let cachedExpiresAtMs = null;

const updateCachedTokenFromSession = (session) => {
  if (!session?.access_token) {
    cachedAccessToken = null;
    cachedExpiresAtMs = null;
    return;
  }
  cachedAccessToken = session.access_token;
  cachedExpiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
};

const getCachedToken = ({ refreshSkewMs = 60_000, allowExpired = false } = {}) => {
  if (!cachedAccessToken) return null;
  if (allowExpired) return cachedAccessToken;
  if (!cachedExpiresAtMs) return cachedAccessToken;
  return Date.now() <= cachedExpiresAtMs - Number(refreshSkewMs) ? cachedAccessToken : null;
};

try {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      updateCachedTokenFromSession(null);
      return;
    }
    updateCachedTokenFromSession(session || null);
  });
} catch {
  // ignore
}

export const getValidAccessToken = async ({
  forceRefresh = false,
  timeoutMs = 7000,
  refreshSkewMs = 60_000,
} = {}) => {
  let session = null;
  try {
    const { data: sessionData } = await withTimeout(
      supabase.auth.getSession(),
      Math.min(timeoutMs, 3000),
      'supabase.auth.getSession'
    );
    session = sessionData?.session || null;
    updateCachedTokenFromSession(session);
  } catch {
    // Fallback to last known token when auth session retrieval is temporarily hung.
    return getCachedToken({ refreshSkewMs, allowExpired: true });
  }

  if (!session?.access_token) {
    return getCachedToken({ refreshSkewMs, allowExpired: true });
  }
  const currentToken = session.access_token;

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
  const needsRefresh =
    Boolean(expiresAtMs) && Date.now() > expiresAtMs - Number(refreshSkewMs);

  if (!forceRefresh && !needsRefresh) {
    return session.access_token;
  }

  // De-duplicate concurrent refreshes (multiple requests can happen on resume).
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        return await withTimeout(
          supabase.auth.refreshSession(),
          timeoutMs,
          'supabase.auth.refreshSession'
        );
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  let refreshRes;
  try {
    refreshRes = await refreshInFlight;
  } catch (err) {
    // Best-effort fallback: keep using the current token.
    // This is important for "finish round" calls — we'd rather try and get a 401
    // (which the caller can retry) than silently skip the request entirely.
    return currentToken || getCachedToken({ refreshSkewMs, allowExpired: true });
  }

  const refreshedSession = refreshRes?.data?.session || null;
  if (!refreshedSession?.access_token) {
    return currentToken || getCachedToken({ refreshSkewMs, allowExpired: true });
  }

  updateCachedTokenFromSession(refreshedSession);
  return refreshedSession.access_token;
};

